/**
 * @flow
 */

import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';

import { GET_MASTERS_QUERY } from './MastersList';

import type { ModalDialog, Master } from '../../../type';

type DeleteMasterDialogProps = {
    master: Master,
} & ModalDialog;

const DELETE_MASTER_MUTATION = gql`
    mutation deleteMaster($input: DeleteMasterInput!) {
        deleteMaster(input: $input) {
            masterId
        }
    }
`;

const DeleteMasterDialog = ({
    master,
    trigger,
    open,
    onClose,
}: DeleteMasterDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const [deleteMaster, { loading }] = useMutation(DELETE_MASTER_MUTATION);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = () => {
        setDeleteFailed(false);
        // No optimisticResponse: the row stays visible while the request is
        // in flight, so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, naturally unmounting
        // this component — no need to call onClose() in .then().
        deleteMaster({
            variables: {
                input: {
                    salonId: salon,
                    masterId: master.id,
                },
            },
            update: (store, { data: { deleteMaster: deleteMasterData } }) => {
                const data = store.readQuery({
                    query: GET_MASTERS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                });

                const nodes = data.viewer.salon.masters.nodes || [];
                store.writeQuery({
                    query: GET_MASTERS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                masters: {
                                    ...data.viewer.salon.masters,
                                    nodes: nodes.filter(
                                        m => m.id !== deleteMasterData.masterId,
                                    ),
                                },
                            },
                        },
                    },
                });
            },
        }).catch(() => {
            setDeleteFailed(true);
        });
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                Delete Master
            </Header>
            <Modal.Content>
                <p>
                    Are you sure you want to delete {master.firstName}{' '}
                    {master.lastName}?
                </p>
                {deleteFailed && <p>Something went wrong</p>}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    No
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    Yes
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteMasterDialog;
